import "./DocumentListItem.css";

import { DownloadDarkIcon, FileCornerBlueIcon, TrashDark } from "../../../../assets";
import { useDocumentListItem } from "./useDocumentListItem";

/** Design-system corner treatment: `seed` = rounded, `hub` = square. */
export type DocumentListVariant = "seed" | "hub";

export interface DocumentListItemProps {
  // ── Document fields (plain values — no API dependency) ──
  documentUuid: string;
  fileName: string;
  /** e.g. "Project design document" — the "[Doc type]" segment of the meta line. */
  documentType?: string;
  /** Pre-formatted size, e.g. "1.2 MB" — the "[File size]" segment of the meta line. */
  fileSize?: string;
  /** Pre-formatted upload date, e.g. "12/03/2026" — rendered as "Uploaded {uploadedAt}". */
  uploadedAt?: string;
  url?: string;

  // ── Presentational / behaviour ──
  /** Corner treatment — defaults to `seed` (rounded). */
  variant?: DocumentListVariant;
  /** Hides the remove (trash) action — the item becomes view/download only. */
  readOnly?: boolean;
  onDownload?: (documentUuid: string) => void;
  onRemove?: (documentUuid: string) => void;
}

export const DocumentListItem = ({
  documentUuid,
  fileName,
  documentType,
  fileSize,
  uploadedAt,
  url,
  variant = "seed",
  readOnly = false,
  onDownload,
  onRemove,
}: DocumentListItemProps): JSX.Element => {
  const { canDownload, handleDownload, handleRemove } = useDocumentListItem({
    documentUuid,
    url,
    onDownload,
    onRemove,
  });

  const secondaryText = [documentType, fileSize, uploadedAt ? `Uploaded ${uploadedAt}` : undefined]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className={`DocumentListItem DocumentListItem_${variant}`}>
      <div className="DocumentListItem_info">
        <FileCornerBlueIcon className="DocumentListItem_fileIcon" />
        <div className="DocumentListItem_text">
          <p className="body1 DocumentListItem_name">{fileName}</p>
          {secondaryText && <p className="body2 DocumentListItem_meta">{secondaryText}</p>}
        </div>
      </div>
      <div className="DocumentListItem_actions">
        {!readOnly && onRemove && (
          <button
            type="button"
            className="DocumentListItem_action DocumentListItem_removeIcon"
            aria-label={`Remove ${fileName}`}
            onClick={handleRemove}
          >
            <TrashDark />
          </button>
        )}
        {canDownload && (
          <button
            type="button"
            className="DocumentListItem_action DocumentListItem_downloadIcon"
            aria-label={`Download ${fileName}`}
            onClick={handleDownload}
          >
            <DownloadDarkIcon />
          </button>
        )}
      </div>
    </div>
  );
};
